import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Shield, Save, Trash2, Mail, Copy, X } from "lucide-react";
import { api } from "../../lib/api";
import { Skeleton, useConfirm } from "./_shared";

/* ---------------- Team (managers direct-create) ---------------- */
export default function TeamTab() {
  const [managers, setManagers] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [showPw, setShowPw] = useState(false);
  const [credsModal, setCredsModal] = useState(null); // { name, email, password }
  const [resetting, setResetting] = useState(null); // { user_id, name, password }
  const { confirm, dialog: confirmDialog } = useConfirm();

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/managers")
      .then((r) => { setManagers(r.data.managers || []); setPending(r.data.pending_invites || []); })
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load team"))
      .finally(() => setLoading(false));
  }, []);
  useEffect(load, [load]);

  const genPassword = () => {
    const chars = "abcdefghjkmnpqrstuvwxyzABCDEFGHJKMNPQRSTUVWXYZ23456789";
    let pw = "";
    const cryptoObj = window.crypto || window.msCrypto;
    const arr = new Uint32Array(12);
    cryptoObj.getRandomValues(arr);
    for (let i = 0; i < 12; i++) pw += chars[arr[i] % chars.length];
    return pw;
  };

  const create = async (e) => {
    e.preventDefault();
    const name = form.name.trim();
    const email = form.email.trim();
    const password = form.password || genPassword();
    if (!name || !email) return toast.error("Name and email are required");
    if (password.length < 8) return toast.error("Password must be at least 8 characters");
    setCreating(true);
    try {
      const { data } = await api.post("/admin/managers/create", { name, email, password, send_email: form.send_email });
      setCredsModal({ name, email, password, email_delivered: !!data?.email_delivered, send_email_requested: form.send_email });
      setForm({ name: "", email: "", password: "", send_email: true });
      if (form.send_email && data?.email_delivered) toast.success(`Manager created — login emailed to ${email}`);
      else if (form.send_email && !data?.email_delivered) toast.warning("Manager created — but email failed to send. Copy credentials below.");
      else toast.success("Manager account created");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create manager");
    } finally {
      setCreating(false);
    }
  };

  const revokeInvite = async (invId) => {
    const ok = await confirm({
      title: "Revoke this pending invite?",
      message: "The invite link will stop working immediately.",
      confirmLabel: "Revoke invite",
      tone: "warning",
    });
    if (!ok) return;
    try {
      await api.delete(`/admin/managers/invite/${invId}`);
      toast.success("Invite revoked");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const removeManager = async (m) => {
    const ok = await confirm({
      title: "Remove Back Office access?",
      message: "They will be signed out and cannot log back in.",
      meta: `${m.name || m.email} · ${m.email}`,
      confirmLabel: "Remove access",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/admin/managers/${m.user_id}`);
      toast.success("Manager access removed");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const startReset = (m) => {
    setResetting({ user_id: m.user_id, name: m.name || m.email, email: m.email, password: genPassword() });
  };

  const submitReset = async () => {
    if (!resetting) return;
    if ((resetting.password || "").length < 8) return toast.error("Password must be at least 8 characters");
    try {
      await api.post(
        `/admin/managers/${resetting.user_id}/reset-password?password=${encodeURIComponent(resetting.password)}`
      );
      setCredsModal({ name: resetting.name, email: resetting.email, password: resetting.password, reset: true });
      setResetting(null);
      toast.success("Password reset");
    } catch (e) { toast.error(e?.response?.data?.detail || "Reset failed"); }
  };

  const copy = (text, label = "Copied") => {
    navigator.clipboard?.writeText(text).then(() => toast.success(label)).catch(() => {});
  };

  const copyBoth = () => {
    if (!credsModal) return;
    const text = `Plenty Rydez · Back Office login\nEmail: ${credsModal.email}\nTemporary password: ${credsModal.password}\nSign in: ${window.location.origin}/login (choose "Manager")`;
    copy(text, "Login details copied");
  };

  return (
    <div className="space-y-8">
      <form onSubmit={create}
        data-testid="team-create-form"
        className="glass rounded-3xl p-5 sm:p-6 border border-lime-400/30">
        <div className="flex items-center gap-2 mb-3">
          <Shield size={16} className="text-lime-400" />
          <div className="label-caps text-lime-400 text-xs">Create a manager account</div>
        </div>
        <p className="text-sm text-white/60 mb-4">
          Create the account instantly with a temporary password. Tick the box below to also email the login details, or share them manually.
        </p>
        <div className="grid sm:grid-cols-2 gap-3 mb-3">
          <input
            data-testid="team-create-name"
            value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="Full name (e.g. Alex Reyes)"
            className="bg-ink-800 border border-white/10 rounded-xl px-4 py-2.5 focus:border-lime-400 outline-none text-sm"
            required />
          <input
            data-testid="team-create-email"
            value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            type="email" placeholder="alex@plenty-rydez.com"
            className="bg-ink-800 border border-white/10 rounded-xl px-4 py-2.5 focus:border-lime-400 outline-none text-sm"
            required />
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 flex items-stretch bg-ink-800 border border-white/10 rounded-xl focus-within:border-lime-400 overflow-hidden">
            <input
              data-testid="team-create-password"
              value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
              type={showPw ? "text" : "password"}
              placeholder="Temporary password (min 8 chars)"
              className="flex-1 bg-transparent px-4 py-2.5 outline-none text-sm" />
            <button type="button"
              data-testid="team-toggle-pw"
              onClick={() => setShowPw((s) => !s)}
              className="px-3 text-xs text-white/50 hover:text-white border-l border-white/10">
              {showPw ? "Hide" : "Show"}
            </button>
            <button type="button"
              data-testid="team-generate-pw"
              onClick={() => { setForm((f) => ({ ...f, password: genPassword() })); setShowPw(true); }}
              className="px-3 text-xs text-lime-400 hover:brightness-110 border-l border-white/10 label-caps">
              Generate
            </button>
          </div>
          <button
            data-testid="team-create-submit"
            disabled={creating}
            className="bg-lime-400 text-ink-900 rounded-full px-5 py-2.5 text-xs font-semibold label-caps hover:brightness-110 disabled:opacity-60 inline-flex items-center justify-center gap-1.5">
            <Shield size={12} /> {creating ? "Creating…" : "Create manager"}
          </button>
        </div>
        <label className="inline-flex items-center gap-2 text-xs text-white/70 mt-3 cursor-pointer">
          <input
            type="checkbox"
            data-testid="team-send-email"
            checked={form.send_email}
            onChange={(e) => setForm((f) => ({ ...f, send_email: e.target.checked }))}
            className="accent-lime-400" />
          <span>Also email the login details to this manager</span>
        </label>
      </form>

      <div>
        <div className="label-caps text-white/60 text-xs mb-3">Active managers ({managers.length})</div>
        {loading ? <Skeleton /> : managers.length === 0 ? (
          <div className="glass rounded-2xl p-8 text-center text-white/50 text-sm">
            No managers yet. Create your first one above.
          </div>
        ) : (
          <div data-testid="team-managers-list" className="grid gap-3">
            {managers.map((m) => (
              <div key={m.user_id} data-testid={`team-manager-${m.user_id}`}
                className="glass rounded-2xl p-4 flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <div className="font-serif text-lg">{m.name || m.email}</div>
                  <div className="text-xs text-white/50 flex items-center gap-1.5">
                    <Mail size={11} /> {m.email}
                    {m.auth_method === "password" && (
                      <span className="label-caps text-[9px] px-1.5 py-0.5 rounded-full bg-lime-400/10 text-lime-300 border border-lime-400/30 ml-2">
                        password
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <button
                    data-testid={`team-reset-${m.user_id}`}
                    onClick={() => startReset(m)}
                    className="px-3 py-1.5 rounded-full text-xs border border-lime-400/40 text-lime-300 hover:bg-lime-400/10 inline-flex items-center gap-1.5">
                    <Save size={12} /> Reset password
                  </button>
                  <button
                    data-testid={`team-remove-${m.user_id}`}
                    onClick={() => removeManager(m)}
                    className="px-3 py-1.5 rounded-full text-xs border border-red-500/40 text-red-300 hover:bg-red-500/10 inline-flex items-center gap-1.5">
                    <Trash2 size={12} /> Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {pending.length > 0 && (
        <div>
          <div className="label-caps text-white/60 text-xs mb-3">Legacy pending invites ({pending.length})</div>
          <div data-testid="team-pending-list" className="grid gap-3">
            {pending.map((inv) => (
              <div key={inv.id}
                data-testid={`team-invite-${inv.id}`}
                className="rounded-2xl border border-orange-500/30 bg-orange-500/5 p-4 flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <div className="font-serif text-base">{inv.name}</div>
                  <div className="text-xs text-white/50">{inv.email}</div>
                  <div className="text-[11px] text-white/40 mt-1">
                    Invited by {inv.invited_by} · older invite (email flow disabled)
                  </div>
                </div>
                <button
                  data-testid={`team-revoke-${inv.id}`}
                  onClick={() => revokeInvite(inv.id)}
                  className="px-3 py-1.5 rounded-full text-xs border border-red-500/40 text-red-300 hover:bg-red-500/10">
                  Revoke
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Reset password modal */}
      {resetting && (
        <div className="fixed inset-0 z-50 bg-ink-950/85 flex items-center justify-center p-6"
          onClick={() => setResetting(null)}
          data-testid="team-reset-modal">
          <div onClick={(e) => e.stopPropagation()}
            className="max-w-md w-full glass rounded-3xl p-6 space-y-4 border border-lime-400/40">
            <div className="flex items-center justify-between">
              <div className="label-caps text-lime-400 text-xs">Reset password · {resetting.name}</div>
              <button onClick={() => setResetting(null)} className="text-white/60 hover:text-white"><X size={16} /></button>
            </div>
            <p className="text-xs text-white/60">
              Enter a new temporary password for this manager. All their existing sessions will be signed out.
            </p>
            <div className="flex items-stretch bg-ink-800 border border-white/10 rounded-xl focus-within:border-lime-400 overflow-hidden">
              <input
                data-testid="team-reset-password"
                value={resetting.password}
                onChange={(e) => setResetting((r) => ({ ...r, password: e.target.value }))}
                type="text"
                placeholder="Temporary password"
                className="flex-1 bg-transparent px-4 py-2.5 outline-none text-sm" />
              <button type="button"
                onClick={() => setResetting((r) => ({ ...r, password: genPassword() }))}
                className="px-3 text-xs text-lime-400 hover:brightness-110 border-l border-white/10 label-caps">
                Generate
              </button>
            </div>
            <div className="flex gap-2 pt-2">
              <button type="button" onClick={() => setResetting(null)}
                className="flex-1 py-2.5 rounded-full text-xs label-caps border border-white/12 text-white/60 hover:border-white/30">Cancel</button>
              <button type="button" onClick={submitReset}
                data-testid="team-reset-submit"
                className="flex-1 py-2.5 rounded-full text-xs label-caps bg-lime-400 text-ink-900 font-semibold hover:brightness-110">
                Reset password
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Credentials share modal */}
      {credsModal && (
        <div
          data-testid="team-creds-modal"
          className="fixed inset-0 z-50 bg-ink-950/85 flex items-center justify-center p-6"
          onClick={() => setCredsModal(null)}
        >
          <div onClick={(e) => e.stopPropagation()}
            className="max-w-md w-full glass rounded-3xl p-6 space-y-4 border border-lime-400/40">
            <div className="flex items-center justify-between">
              <div className="label-caps text-lime-400 text-xs">{credsModal.reset ? "Password reset" : "Manager created"}</div>
              <button onClick={() => setCredsModal(null)} className="text-white/60 hover:text-white"><X size={16} /></button>
            </div>
            <div className="text-sm text-white/70">
              {credsModal.send_email_requested && credsModal.email_delivered ? (
                <>Login details have been <span className="text-lime-300 font-semibold">emailed</span> to <span className="text-white font-semibold">{credsModal.name}</span>. You can also share them manually below.</>
              ) : credsModal.send_email_requested && !credsModal.email_delivered ? (
                <>Manager was created but the <span className="text-amber-300">email failed to send</span>. Share these details with <span className="text-white font-semibold">{credsModal.name}</span> manually.</>
              ) : (
                <>Share these details with <span className="text-white font-semibold">{credsModal.name}</span> so they can sign into the Back Office. This is the only time the password will be shown.</>
              )}
            </div>
            <div className="rounded-xl border border-white/10 bg-ink-950/70 p-4 space-y-3 text-sm">
              <div>
                <div className="label-caps text-white/40 text-[10px]">Email</div>
                <div className="flex items-center justify-between gap-2">
                  <div className="text-white break-all">{credsModal.email}</div>
                  <button onClick={() => copy(credsModal.email)} className="text-lime-400 hover:brightness-110 text-xs inline-flex items-center gap-1">
                    <Copy size={11} /> Copy
                  </button>
                </div>
              </div>
              <div>
                <div className="label-caps text-white/40 text-[10px]">Temporary password</div>
                <div className="flex items-center justify-between gap-2">
                  <div className="font-mono text-lime-300 text-base break-all">{credsModal.password}</div>
                  <button
                    data-testid="team-creds-copy-password"
                    onClick={() => copy(credsModal.password, "Password copied")}
                    className="text-lime-400 hover:brightness-110 text-xs inline-flex items-center gap-1">
                    <Copy size={11} /> Copy
                  </button>
                </div>
              </div>
              <div className="text-[11px] text-white/50 pt-2 border-t border-white/10">
                Sign in at <span className="text-white/80">{window.location.origin}/login</span> and choose the <span className="text-white/80">Manager</span> tab.
              </div>
            </div>
            <button
              data-testid="team-creds-copy-all"
              onClick={copyBoth}
              className="btn-gold w-full py-2.5 rounded-full text-xs inline-flex items-center justify-center gap-1.5">
              <Copy size={12} /> Copy all login details
            </button>
          </div>
        </div>
      )}
      {confirmDialog}
    </div>
  );
}



